import React from 'react'
import { Validation, ValidationError } from '@hookstate/validation';
import { useLanguageState } from '../../globalStates/LanguageState';
import { useExhibitorDataState } from '../../globalStates/ExhibitorDataState';
import ModalOk from '../../components/Modal/ModalOk';
import { isNullOrEmpty } from '../../utils/Arrays';
import { isNumeric } from '../../utils';
import { Branding, ValidationFieldNamesConfiguration } from '../../branding/branding';
import styled from 'styled-components';

const ValidationsWrapper = styled.div`
    & > .error-groups {
        padding-left: 0;
        .error-group {
            list-style-type: none;
            & > label {
                font-weight: bold;
            }
        }
    }
`

interface ErrorField {
    name: string
}

interface ErrorSection {
    name: string
    index?: number
    fields: ErrorField[]
    sections: ErrorSection[]
}

interface ErrorGroup {
    name: string
    sections: ErrorSection[]
}

export interface ErrorSectionLookup {
    [key: string]: ErrorSection
}

function makeKey(name: string | number, index: string | number, prefix: string) {
    return `${prefix}|${name.toString()}|${(isNumeric(index) ? index.toString() : '')}`
}

function findCreateSection(error: ValidationError, sectionsCache: ErrorSectionLookup, offset: number, keyPrefix: string = ''): [boolean, ErrorSection, string] {
    const sectionsKey = makeKey(error.path[offset], error.path[offset + 1], keyPrefix)
    let section = sectionsCache[sectionsKey]
    const isNew = !section
    if (isNew) {
        section = {
            name: error.path[offset] as string,
            index: error.path.length === 4 || error.path.length === 6 ? error.path[offset + 1] as number : undefined,
            fields: [],
            sections: []
        }
        sectionsCache[sectionsKey] = section
    }
    return [isNew, section, sectionsKey]
}

function mapErrors(errors: readonly ValidationError[]): ErrorSection[] {
    const sectionsCache: ErrorSectionLookup = {}
    const errorSections: ErrorSection[] = []
    errors.forEach(error => {
        const [isSectionNew, rootSection, sectionsKey] = findCreateSection(error, sectionsCache, error.path.length === 2 ? 0 : 1)
        switch (error.path.length) {
            case 2:
                rootSection.fields.push({
                    name: error.path[1] as string
                })
                break
            case 3:
                rootSection.fields.push({
                    name: error.path[2] as string
                })
                break
            case 4:
                rootSection.fields.push({
                    name: error.path[3] as string
                })
                break
            case 6:
                const [isSubSectionNew, subSection] = findCreateSection(error, sectionsCache, 3, sectionsKey)
                subSection.fields.push({
                    name: error.path[5] as string
                })
                if (isSubSectionNew) rootSection.sections.push(subSection)
                break
        }
        if (isSectionNew) errorSections.push(rootSection)
    })

    return errorSections
}

function groupErrors(errorSections: ErrorSection[]): ErrorGroup[] {
    const groups: ErrorGroup[] = []
    const groupsLookup: { [key: string]: ErrorGroup } = {}
    errorSections.forEach(errorSection => {
        let group = groupsLookup[errorSection.name]
        if (!group) {
            group = { name: errorSection.name, sections: [] }
            groupsLookup[errorSection.name] = group
            groups.push(group)
        }
        group.sections.push(errorSection)
    })

    return groups
}

function renderField(branding: Branding, sectionName: string, errorField: ErrorField) {
    const brandedSection = branding.configuration.modalPublish.fieldNamesConfig[sectionName as keyof ValidationFieldNamesConfiguration]
    const brandedFieldNames = brandedSection ? brandedSection.fieldNames : []
    const brandedFieldName = brandedFieldNames.find(field => field.name === errorField.name)
    return (
        <li key={`${sectionName}-${errorField.name}`} className='error-field'>
            <label className='error-field-name'>{brandedFieldName?.value || errorField.name}</label>
        </li>
    )
}

function renderFields(branding: Branding, sectionName: string, errorFields: ErrorField[]) {
    return isNullOrEmpty(errorFields) ? null : (
        <ul className='error-fields'>
            {errorFields?.map(error => renderField(branding, sectionName, error))}
        </ul>
    )
}

function renderSection(branding: Branding, errorSection: ErrorSection) {
    const brandedGroup = branding.configuration.modalPublish.fieldNamesConfig[errorSection.name as keyof ValidationFieldNamesConfiguration]
    const brandedGroupName = `${brandedGroup.nameIndex ?? errorSection.name} - ${errorSection.index === undefined ? '' : errorSection.index + 1}`
    return (
        <li key={`${errorSection.name}-${errorSection.index ?? ''}`} className='error-section'>
            <label className='error-section-name'>{brandedGroupName}</label>
            {renderFields(branding, errorSection.name, errorSection.fields)}
            {renderSections(branding, errorSection.sections)}
        </li>
    )
}

function renderSections(branding: Branding, errorSections?: ErrorSection[]) {
    return isNullOrEmpty(errorSections) ? null : (
        <ul className='error-sections'>
            {errorSections?.map(error => renderSection(branding, error))}
        </ul>
    )
}

function renderGroup(branding: Branding, errorGroup: ErrorGroup) {
    const brandedGroup = branding.configuration.modalPublish.fieldNamesConfig[errorGroup.name as keyof ValidationFieldNamesConfiguration]
    if (brandedGroup) {
        const brandedGroupName = brandedGroup.name ?? errorGroup.name
        return (
            <li key={errorGroup.name} className='error-group'>
                <label className='error-group-name'>{brandedGroupName}</label>
                {errorGroup.sections && errorGroup.sections.length === 1 && errorGroup.sections[0].index === undefined ? renderFields(branding, errorGroup.sections[0].name, errorGroup.sections[0].fields) : renderSections(branding, errorGroup.sections)}
            </li>
        )
    }
    return (
        <li key={errorGroup.name} className='error-group'>
            <label className='error-group-name'>Missing branding config for {errorGroup.name}</label>
        </li>
    )
}

function renderGroups(branding: Branding, errorGroups?: ErrorGroup[]) {
    return isNullOrEmpty(errorGroups) ? null : (
        <ul className='error-groups'>
            {errorGroups?.map(error => renderGroup(branding, error))}
        </ul>
    )
}

interface FailedValidationModalProps {
    visible: boolean
    onClose: () => void
}

const FailedValidationModal: React.FC<FailedValidationModalProps> = ({ visible, onClose }) => {
    const branding = useLanguageState().getStrings()
    const exiState = useExhibitorDataState()
    const errors = Validation(exiState.getOrganization()).errors()
    return (
        <ModalOk
            name="save_errors"
            title={branding.configuration.modalPublish.title}
            visible={visible}
            onOk={onClose}
            onClose={onClose}
        >
            <label>{branding.configuration.modalPublish.label}</label>
            <ValidationsWrapper>
                {renderGroups(branding, groupErrors(mapErrors(errors)))}
            </ValidationsWrapper>
        </ModalOk>
    )
}

export default FailedValidationModal
